import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";

/**
 * GLP-1 protein-per-meal status (BW18 PR-I).
 *
 * Renders only when athlete_profile.on_glp1 is true. Shows today's meals
 * with each one's protein, flagged if below 25g. Per-meal target is the
 * daily protein target / 4 feedings; if no target is set we fall back to
 * 1.6 g/kg of current weight.
 */

type MealRow = {
  id: string;
  ts: string;
  name: string;
  macros: { p?: number } | null;
};

const MIN_PER_MEAL_GRAMS = 25;
const TARGET_FEEDINGS = 4;

export function Glp1ProteinStatus() {
  const profile = useAthleteProfile();
  const onGlp1 = Boolean(profile.data?.on_glp1);
  const { session } = useSession();
  const userId = session?.user?.id;

  const meals = useQuery({
    queryKey: ["glp1_today_meals", userId],
    enabled: onGlp1 && Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<MealRow[]> => {
      if (!userId) return [];
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      const { data, error } = await supabase
        .from("meal")
        .select("id, ts, name, macros")
        .eq("user_id", userId)
        .gte("ts", start.toISOString())
        .lt("ts", end.toISOString())
        .order("ts", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MealRow[];
    },
  });

  if (!onGlp1) return null;

  const dailyTarget = computePerDayTarget(profile.data);
  const perMealTarget = Math.max(
    MIN_PER_MEAL_GRAMS,
    Math.round(dailyTarget / TARGET_FEEDINGS),
  );
  const rows = meals.data ?? [];
  const totalP = rows.reduce(
    (s, m) => s + Number(m.macros?.p ?? 0),
    0,
  );
  const lowMeals = rows.filter((m) => Number(m.macros?.p ?? 0) < MIN_PER_MEAL_GRAMS).length;

  return (
    <section
      aria-labelledby="glp1-protein-status-heading"
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-surface)",
        padding: "var(--space-5) var(--space-6)",
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "var(--space-3)",
        }}
      >
        <h3
          id="glp1-protein-status-heading"
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "var(--type-body)",
            color: "var(--fg)",
          }}
        >
          Protein meal status
        </h3>
        <Link
          to="/health/glp1"
          style={{
            color: "var(--accent)",
            fontSize: "var(--type-meta)",
          }}
        >
          GLP-1 →
        </Link>
      </header>

      {rows.length === 0 ? (
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No meals logged today. Aim for {perMealTarget}g protein per feeding.
        </p>
      ) : (
        <>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {rows.map((m) => {
              const p = Number(m.macros?.p ?? 0);
              const low = p < MIN_PER_MEAL_GRAMS;
              return (
                <li
                  key={m.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr max-content",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    padding: "6px 0",
                    borderBottom: "1px solid var(--hair)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--fg)",
                      fontSize: "var(--type-body-s)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.name || "Meal"}
                  </span>
                  <span
                    className="mono"
                    style={{
                      color: low ? "var(--brick)" : "var(--fg-2)",
                      fontSize: "var(--type-body-s)",
                    }}
                    aria-label={low ? "below 25g protein" : undefined}
                  >
                    {Math.round(p)}g{low ? " ↓" : ""}
                  </span>
                </li>
              );
            })}
          </ul>
          <footer
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "var(--type-body-s)",
              color: "var(--fg-2)",
            }}
          >
            <span>
              Today total{" "}
              <span className="mono" style={{ color: "var(--fg)" }}>
                {Math.round(totalP)}g
              </span>{" "}
              · daily target{" "}
              <span className="mono" style={{ color: "var(--fg-3)" }}>
                {dailyTarget}g
              </span>
            </span>
            <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
              Per meal target {perMealTarget}g
              {lowMeals > 0 && (
                <>
                  {" · "}
                  <span style={{ color: "var(--brick)" }}>
                    {lowMeals} below
                  </span>
                </>
              )}
            </span>
          </footer>
        </>
      )}
    </section>
  );
}

function computePerDayTarget(
  profile: { current_weight_kg?: number | null } | null | undefined,
): number {
  const weight = profile?.current_weight_kg;
  if (typeof weight === "number" && weight > 0) {
    return Math.round(weight * 1.6);
  }
  return 125;
}
